import React from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  ChevronRightIcon, 
  ChevronLeftIcon, 
  AcademicCapIcon,
  PlusIcon,
  TrashIcon,
  SparklesIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

// Base validation schema for Step 2
const getStep2Schema = (canSkip: boolean = false) => {
  if (canSkip) {
    return yup.object({
      education: yup.object({
        tenth: yup.object({
          board: yup.string().trim().optional(),
          year: yup.string().optional().test('valid-year', 'Enter valid 4-digit year', (val) => !val || /^\d{4}$/.test(val)),
          percentage: yup.number()
            .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
            .optional()
            .nullable()
            .min(0, 'Percentage cannot be negative')
            .max(100, 'Percentage cannot exceed 100'),
          school: yup.string().trim().optional()
        }).optional(),
        twelfth: yup.object({
          board: yup.string().trim().optional(),
          year: yup.string().optional().test('valid-year', 'Enter valid 4-digit year', (val) => !val || /^\d{4}$/.test(val)),
          percentage: yup.number()
            .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
            .optional()
            .nullable()
            .min(0, 'Percentage cannot be negative')
            .max(100, 'Percentage cannot exceed 100'),
          school: yup.string().trim().optional()
        }).optional(),
        graduation: yup.object({
          university: yup.string().trim().nullable().optional(),
          year: yup.string().nullable().optional().when('university', {
            is: (val: string) => Boolean(val && val.trim().length > 0),
            then: (schema) => schema.required('Year is required').matches(/^\d{4}$/, 'Enter valid 4-digit year'),
            otherwise: (schema) => schema.notRequired()
          }),
          percentage: yup.number()
            .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
            .nullable()
            .optional()
            .when('university', {
              is: (val: string) => Boolean(val && val.trim().length > 0),
              then: (schema) => schema.typeError('Percentage is required').required('Percentage is required').min(0).max(100),
              otherwise: (schema) => schema.notRequired()
            }),
          college: yup.string().trim().nullable().optional().when('university', {
            is: (val: string) => Boolean(val && val.trim().length > 0),
            then: (schema) => schema.required('College name is required'),
            otherwise: (schema) => schema.notRequired()
          }),
          degree: yup.string().trim().nullable().optional().when('university', {
            is: (val: string) => Boolean(val && val.trim().length > 0),
            then: (schema) => schema.required('Degree is required'),
            otherwise: (schema) => schema.notRequired()
          })
        }).nullable().optional(),
        bped: yup.object({
          university: yup.string().trim().nullable().optional(),
          year: yup.string().nullable().optional().when('university', {
            is: (val: string) => Boolean(val && val.trim().length > 0),
            then: (schema) => schema.required('Year is required').matches(/^\d{4}$/, 'Enter valid 4-digit year'),
            otherwise: (schema) => schema.notRequired()
          }),
          percentage: yup.number()
            .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
            .nullable()
            .optional()
            .when('university', {
              is: (val: string) => Boolean(val && val.trim().length > 0),
              then: (schema) => schema.typeError('Percentage is required').required('Percentage is required').min(0).max(100),
              otherwise: (schema) => schema.notRequired()
            }),
          college: yup.string().trim().nullable().optional().when('university', {
            is: (val: string) => Boolean(val && val.trim().length > 0),
            then: (schema) => schema.required('College name is required'),
            otherwise: (schema) => schema.notRequired()
          })
        }).nullable().optional(),
        others: yup.array(
          yup.object({
            qualification: yup.string().trim().required('Qualification is required'),
            institution: yup.string().trim().required('Institution is required'),
            year: yup.string().required('Year is required').matches(/^\d{4}$/, 'Enter valid 4-digit year'),
            percentage: yup.number()
              .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
              .typeError('Percentage is required')
              .required('Percentage is required')
              .min(0)
              .max(100)
          })
        ).nullable().optional()
      }).optional()
    });
  }

  return yup.object({
    education: yup.object({
      tenth: yup.object({
        board: yup.string().trim().required('Board is required'),
        year: yup.string().required('Year is required').matches(/^\d{4}$/, 'Enter valid 4-digit year'),
        percentage: yup.number()
          .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
          .typeError('Percentage is required and must be a number')
          .required('Percentage is required')
          .min(0, 'Percentage cannot be negative')
          .max(100, 'Percentage cannot exceed 100'),
        school: yup.string().trim().required('School name is required')
      }),
      twelfth: yup.object({
        board: yup.string().trim().required('Board is required'),
        year: yup.string().required('Year is required').matches(/^\d{4}$/, 'Enter valid 4-digit year'),
        percentage: yup.number()
          .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
          .typeError('Percentage is required and must be a number')
          .required('Percentage is required')
          .min(0, 'Percentage cannot be negative')
          .max(100, 'Percentage cannot exceed 100'),
        school: yup.string().trim().required('School name is required')
      }),
      graduation: yup.object({
        university: yup.string().trim().nullable().optional(),
        year: yup.string().nullable().optional().when('university', {
          is: (val: string) => Boolean(val && val.trim().length > 0),
          then: (schema) => schema.required('Year is required').matches(/^\d{4}$/, 'Enter valid 4-digit year'),
          otherwise: (schema) => schema.notRequired()
        }),
        percentage: yup.number()
          .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
          .nullable()
          .optional()
          .when('university', {
            is: (val: string) => Boolean(val && val.trim().length > 0),
            then: (schema) => schema.typeError('Percentage is required').required('Percentage is required').min(0).max(100),
            otherwise: (schema) => schema.notRequired()
          }),
        college: yup.string().trim().nullable().optional().when('university', {
          is: (val: string) => Boolean(val && val.trim().length > 0),
          then: (schema) => schema.required('College name is required'),
          otherwise: (schema) => schema.notRequired()
        }),
        degree: yup.string().trim().nullable().optional().when('university', {
          is: (val: string) => Boolean(val && val.trim().length > 0),
          then: (schema) => schema.required('Degree is required'),
          otherwise: (schema) => schema.notRequired()
        })
      }).nullable().optional(),
      bped: yup.object({
        university: yup.string().trim().nullable().optional(),
        year: yup.string().nullable().optional().when('university', {
          is: (val: string) => Boolean(val && val.trim().length > 0),
          then: (schema) => schema.required('Year is required').matches(/^\d{4}$/, 'Enter valid 4-digit year'),
          otherwise: (schema) => schema.notRequired()
        }),
        percentage: yup.number()
          .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
          .nullable()
          .optional()
          .when('university', {
            is: (val: string) => Boolean(val && val.trim().length > 0),
            then: (schema) => schema.typeError('Percentage is required').required('Percentage is required').min(0).max(100),
            otherwise: (schema) => schema.notRequired()
          }),
        college: yup.string().trim().nullable().optional().when('university', {
          is: (val: string) => Boolean(val && val.trim().length > 0),
          then: (schema) => schema.required('College name is required'),
          otherwise: (schema) => schema.notRequired()
        })
      }).nullable().optional(),
      others: yup.array(
        yup.object({
          qualification: yup.string().trim().required('Qualification is required'),
          institution: yup.string().trim().required('Institution is required'),
          year: yup.string().required('Year is required').matches(/^\d{4}$/, 'Enter valid 4-digit year'),
          percentage: yup.number()
            .transform((val, orig) => (orig === '' || orig === null || orig === undefined ? undefined : val))
            .typeError('Percentage is required')
            .required('Percentage is required')
            .min(0)
            .max(100)
        })
      ).nullable().optional()
    })
  });
};

type EducationData = yup.InferType<ReturnType<typeof getStep2Schema>>;

interface EducationStepProps {
  data: Partial<EducationData>;
  canSkip?: boolean;
  onNext: (data: EducationData) => void;
  onSkip?: () => void;
  onSkipAndSubmit?: () => void;
  onBack: (data?: any) => void;
  isSubmitting?: boolean;
}

const EducationStep: React.FC<EducationStepProps> = ({ 
  data, 
  canSkip = true, 
  onNext, 
  onSkip, 
  onSkipAndSubmit, 
  onBack, 
  isSubmitting 
}) => {
  const { 
    register, 
    handleSubmit, 
    formState: { errors },
    control,
    watch,
    getValues
  } = useForm<EducationData>({
    resolver: yupResolver(getStep2Schema(canSkip)),
    defaultValues: {
      education: {
        tenth: {
          board: '',
          year: '',
          percentage: undefined as any,
          school: ''
        },
        twelfth: {
          board: '',
          year: '',
          percentage: undefined as any,
          school: ''
        },
        graduation: {
          university: '',
          year: '',
          percentage: undefined as any,
          college: '',
          degree: ''
        },
        bped: {
          university: '',
          year: '',
          percentage: undefined as any,
          college: ''
        },
        others: [],
        ...data.education
      }
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'education.others'
  });

  const watchGraduationUniversity = watch('education.graduation.university');
  const watchBpedUniversity = watch('education.bped.university');

  const onSubmit = (formData: EducationData) => {
    // Clean up optional fields that are empty
    const cleanedData = { ...formData };
    
    if (!watchGraduationUniversity) {
      if (cleanedData.education) cleanedData.education.graduation = undefined;
    }
    
    if (!watchBpedUniversity) {
      if (cleanedData.education) cleanedData.education.bped = undefined;
    }

    if (canSkip && cleanedData.education) {
      if (cleanedData.education.tenth && !cleanedData.education.tenth.board && !cleanedData.education.tenth.school) {
        cleanedData.education.tenth = undefined;
      }
      if (cleanedData.education.twelfth && !cleanedData.education.twelfth.board && !cleanedData.education.twelfth.school) {
        cleanedData.education.twelfth = undefined;
      }
    }

    onNext(cleanedData);
  };

  const handleBack = () => {
    const currentValues = getValues();
    onBack(currentValues);
  };

  const addOtherQualification = () => {
    append({
      qualification: '',
      institution: '',
      year: '',
      percentage: 0
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 bg-success-600 rounded-full flex items-center justify-center mr-4">
            <AcademicCapIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Educational Qualifications</h2>
            <p className="text-gray-600">Please provide your educational background</p>
          </div>
        </div>

        {canSkip && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-300 rounded-xl flex flex-wrap items-center justify-between gap-3 text-emerald-900">
            <div className="flex items-start space-x-3">
              <SparklesIcon className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-bold text-emerald-900">Education Details Are Optional</h4>
                <p className="text-xs text-emerald-700 mt-0.5">
                  Entering educational qualifications is optional. You can enter details if available, or skip this step to proceed to document upload or submit directly.
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {onSkip && (
                <button
                  type="button"
                  onClick={onSkip}
                  className="px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-xs font-semibold rounded-lg transition-colors"
                >
                  Skip Education Details →
                </button>
              )}
              {onSkipAndSubmit && (
                <button
                  type="button"
                  onClick={onSkipAndSubmit}
                  disabled={isSubmitting}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors flex items-center space-x-1 disabled:opacity-50"
                >
                  <CheckIcon className="w-3.5 h-3.5" />
                  <span>{isSubmitting ? 'Submitting...' : 'Skip & Submit Application'}</span>
                </button>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* 10th Standard */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm mr-3">10</span>
              10th Standard {!canSkip ? <span className="text-red-500 ml-1">*</span> : <span className="text-xs text-emerald-700 font-normal ml-2">(Optional)</span>}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="form-label">Board <span className="text-red-500">*</span></label>
                <select
                  {...register('education.tenth.board')}
                  className={`form-input ${errors.education?.tenth?.board ? 'border-red-500' : ''}`}
                >
                  <option value="">Select Board</option>
                  <option value="BSE Odisha">BSE Odisha</option>
                  <option value="CBSE">CBSE</option>
                  <option value="ICSE">ICSE</option>
                  <option value="Other">Other</option>
                </select>
                {errors.education?.tenth?.board && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.tenth.board.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Year <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  {...register('education.tenth.year')}
                  className={`form-input ${errors.education?.tenth?.year ? 'border-red-500' : ''}`}
                  placeholder="e.g., 2020"
                  maxLength={4}
                />
                {errors.education?.tenth?.year && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.tenth.year.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Percentage <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  {...register('education.tenth.percentage')}
                  className={`form-input ${errors.education?.tenth?.percentage ? 'border-red-500' : ''}`}
                  placeholder="e.g., 85.5"
                />
                {errors.education?.tenth?.percentage && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.tenth.percentage.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">School Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  {...register('education.tenth.school')}
                  className={`form-input ${errors.education?.tenth?.school ? 'border-red-500' : ''}`}
                  placeholder="Enter school name"
                />
                {errors.education?.tenth?.school && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.tenth.school.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* 12th Standard */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="flex items-center mb-4">
              <AcademicCapIcon className="w-5 h-5 text-primary-600 mr-2" />
              <span className="font-semibold text-gray-900">12th Standard</span> {!canSkip ? <span className="text-red-500 text-sm ml-2">*</span> : <span className="text-xs text-emerald-700 font-normal ml-2">(Optional)</span>}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="form-label">Board</label>
                <select
                  {...register('education.twelfth.board')}
                  className={`form-input ${errors.education?.twelfth?.board ? 'border-red-500' : ''}`}
                >
                  <option value="">Select Board</option>
                  <option value="CHSE Odisha">CHSE Odisha</option>
                  <option value="CBSE">CBSE</option>
                  <option value="ISC">ISC</option>
                  <option value="Other">Other</option>
                </select>
                {errors.education?.twelfth?.board && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.twelfth.board.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Year</label>
                <input
                  type="text"
                  {...register('education.twelfth.year')}
                  className={`form-input ${errors.education?.twelfth?.year ? 'border-red-500' : ''}`}
                  placeholder="e.g., 2022"
                  maxLength={4}
                />
                {errors.education?.twelfth?.year && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.twelfth.year.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Percentage</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  {...register('education.twelfth.percentage')}
                  className={`form-input ${errors.education?.twelfth?.percentage ? 'border-red-500' : ''}`}
                  placeholder="e.g., 85.5"
                />
                {errors.education?.twelfth?.percentage && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.twelfth.percentage.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">School Name</label>
                <input
                  type="text"
                  {...register('education.twelfth.school')}
                  className={`form-input ${errors.education?.twelfth?.school ? 'border-red-500' : ''}`}
                  placeholder="Enter school name"
                />
                {errors.education?.twelfth?.school && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.twelfth.school.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Graduation - Optional */}
          <div className="bg-green-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm mr-3">G</span>
              Graduation <span className="text-gray-500 text-sm ml-2">(Optional)</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div>
                <label className="form-label">University</label>
                <input
                  type="text"
                  {...register('education.graduation.university')}
                  className={`form-input ${errors.education?.graduation?.university ? 'border-red-500' : ''}`}
                  placeholder="Enter university name"
                />
                {errors.education?.graduation?.university && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.graduation.university.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Year</label>
                <input
                  type="text"
                  {...register('education.graduation.year')}
                  className={`form-input ${errors.education?.graduation?.year ? 'border-red-500' : ''}`}
                  placeholder="e.g., 2025"
                  maxLength={4}
                />
                {errors.education?.graduation?.year && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.graduation.year.message}</p>
                )}
              </div>

                <div>
                <label className="form-label">Percentage</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  {...register('education.graduation.percentage')}
                  className={`form-input ${errors.education?.graduation?.percentage ? 'border-red-500' : ''}`}
                  placeholder="e.g., 85.5"
                />
                {errors.education?.graduation?.percentage && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.graduation.percentage.message}</p>
                )}
                </div>

              <div>
                <label className="form-label">College Name</label>
                <input
                  type="text"
                  {...register('education.graduation.college')}
                  className={`form-input ${errors.education?.graduation?.college ? 'border-red-500' : ''}`}
                  placeholder="Enter college name"
                />
                {errors.education?.graduation?.college && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.graduation.college.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Degree</label>
                <input
                  type="text"
                  {...register('education.graduation.degree')}
                  className={`form-input ${errors.education?.graduation?.degree ? 'border-red-500' : ''}`}
                  placeholder="e.g., B.A., B.Sc., B.Com"
                />
                {errors.education?.graduation?.degree && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.graduation.degree.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* B.P.Ed - Optional */}
          <div className="bg-yellow-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-6 h-6 bg-yellow-600 text-white rounded-full flex items-center justify-center text-sm mr-3">B</span>
              B.P.Ed <span className="text-gray-500 text-sm ml-2">(Optional)</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="form-label">University</label>
                <input
                  type="text"
                  {...register('education.bped.university')}
                  className={`form-input ${errors.education?.bped?.university ? 'border-red-500' : ''}`}
                  placeholder="Enter university name"
                />
                {errors.education?.bped?.university && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.bped.university.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Year</label>
                <input
                  type="text"
                  {...register('education.bped.year')}
                  className={`form-input ${errors.education?.bped?.year ? 'border-red-500' : ''}`}
                  placeholder="e.g., 2025"
                  maxLength={4}
                />
                {errors.education?.bped?.year && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.bped.year.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Percentage</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  {...register('education.bped.percentage')}
                  className={`form-input ${errors.education?.bped?.percentage ? 'border-red-500' : ''}`}
                  placeholder="e.g., 85.5"
                />
                {errors.education?.bped?.percentage && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.bped.percentage.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">College Name</label>
                <input
                  type="text"
                  {...register('education.bped.college')}
                  className={`form-input ${errors.education?.bped?.college ? 'border-red-500' : ''}`}
                  placeholder="Enter college name"
                />
                {errors.education?.bped?.college && (
                  <p className="text-red-500 text-sm mt-1">{errors.education.bped.college.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Other Qualifications */}
          <div className="bg-purple-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm mr-3">+</span>
                Other Qualifications <span className="text-gray-500 text-sm ml-2">(Optional)</span>
              </h3>
              <button
                type="button"
                onClick={addOtherQualification}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex items-center space-x-2"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Qualification</span>
              </button>
            </div>

            {fields.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No additional qualifications added</p>
            ) : (
              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div key={field.id} className="bg-white rounded-lg p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900">Qualification {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="form-label">Qualification</label>
                        <input
                          type="text"
                          {...register(`education.others.${index}.qualification`)}
                          className="form-input"
                          placeholder="e.g., Diploma, Certificate"
                        />
                      </div>

                      <div>
                        <label className="form-label">Institution</label>
                        <input
                          type="text"
                          {...register(`education.others.${index}.institution`)}
                          className="form-input"
                          placeholder="Enter institution name"
                        />
                      </div>

                      <div>
                        <label className="form-label">Year</label>
                        <input
                          type="text"
                          {...register(`education.others.${index}.year`)}
                          className="form-input"
                          placeholder="e.g., 2023"
                          maxLength={4}
                        />
                      </div>

                      <div>
                        <label className="form-label">Percentage</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          {...register(`education.others.${index}.percentage`)}
                          className="form-input"
                          placeholder="e.g., 85.5"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={handleBack}
              className="btn-secondary flex items-center space-x-2"
            >
              <ChevronLeftIcon className="w-5 h-5" />
              <span>Back to Personal Details</span>
            </button>
            
            <div className="flex flex-wrap items-center gap-3 ml-auto">
              {canSkip && onSkip && (
                <button
                  type="button"
                  onClick={onSkip}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-4 py-2.5 rounded-lg text-sm transition-colors"
                >
                  Skip Education Details
                </button>
              )}
              {canSkip && onSkipAndSubmit && (
                <button
                  type="button"
                  onClick={onSkipAndSubmit}
                  disabled={isSubmitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2.5 rounded-lg text-sm shadow-sm transition-colors flex items-center space-x-2 disabled:opacity-50"
                  title="Submit application directly, skipping document upload"
                >
                  <CheckIcon className="w-5 h-5" />
                  <span>{isSubmitting ? 'Submitting Application...' : 'Skip to Submit Application'}</span>
                </button>
              )}
              <button
                type="submit"
                className="btn-primary flex items-center space-x-2"
              >
                <span>Continue to Documents</span>
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EducationStep;